<template>
    <v-container fluid class="py-6 px-6 mcp-page">
        <v-row>
            <v-col cols="12">
                <div class="home-like-header mb-4">
                    <div class="header-eyebrow mb-1">MCP Server</div>
                    <h2 class="text-h5 mb-1">Quaro in your AI system</h2>
                    <p class="mb-0 mcp-help-text">
                        Use Quaro inside Claude, ChatGPT, Perplexity, Cursor and other AI assistants. Ask about your keywords, rankings and AI visibility in plain language and let the assistant
                        pull the numbers, compare them and write up the answer.
                    </p>
                </div>
            </v-col>
        </v-row>

        <!-- value proposition -->
        <v-row>
            <v-col cols="12" md="4" v-for="benefit in benefits" :key="benefit.title">
                <v-card outlined class="pa-4 mcp-card fill-height">
                    <v-icon color="primary" class="mb-2">{{ benefit.icon }}</v-icon>
                    <div class="font-weight-bold mb-1">{{ benefit.title }}</div>
                    <p class="mb-0 mcp-help-text">{{ benefit.text }}</p>
                </v-card>
            </v-col>
        </v-row>

        <!-- read only notice -->
        <v-row class="mt-1">
            <v-col cols="12">
                <v-card outlined class="pa-4 mcp-card read-only-card">
                    <div class="d-flex align-start">
                        <v-icon color="primary" class="mr-3 mt-1">mdi-shield-check-outline</v-icon>
                        <div>
                            <div class="font-weight-bold mb-1">Your assistant can read and change your data</div>
                            <p class="mb-0 mcp-help-text">
                                The connection gives your assistant access to your Quaro data with your user rights. It can create, edit and delete collections, projects, prompts and
                                briefings. Deleting cannot be undone, so check what your assistant is about to delete. Actions that use credits count against your plan like in the app.
                            </p>
                        </div>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <!-- compatibility -->
        <v-row class="mt-1">
            <v-col cols="12">
                <v-card outlined class="pa-4 mcp-card">
                    <div class="font-weight-bold text-h6 mb-1">Which AI systems work with Quaro</div>
                    <p class="mb-4 mcp-help-text">
                        Quaro connects through MCP, the open standard most AI assistants use for external tools. If your assistant can add a custom MCP server or connector, it can use Quaro.
                    </p>

                    <v-row>
                        <v-col cols="12" md="4" v-for="tier in compatibility" :key="tier.title">
                            <div class="tier-block" :class="tier.cssClass">
                                <div class="tier-title mb-2">
                                    <v-icon small class="mr-1" :color="tier.iconColor">{{ tier.icon }}</v-icon>
                                    {{ tier.title }}
                                </div>
                                <div v-for="entry in tier.entries" :key="entry.name" class="tier-entry">
                                    <span class="tier-entry-name">{{ entry.name }}</span>
                                    <span class="tier-entry-note">{{ entry.note }}</span>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>
        </v-row>

        <!-- connection -->
        <v-row class="mt-1">
            <v-col cols="12">
                <v-card outlined class="pa-4 mcp-card">
                    <div class="font-weight-bold text-h6 mb-1">{{ canCreateConnectionUrl ? "Create your connection URL" : "Your connection URL" }}</div>
                    <p v-if="canCreateConnectionUrl" class="mb-4 mcp-help-text">
                        To connect an AI assistant you need a personal connection URL. One click creates it, including the API key it needs. You do not have to do anything with the key yourself.
                    </p>
                    <p v-else-if="apiKey || !usePersonalKey" class="mb-4 mcp-help-text">
                        <template v-if="usePersonalKey"
                            >One URL is all your assistant needs. It already contains your personal API key for this company, so treat it like a password.</template
                        >
                        <template v-else
                            >One URL is all your assistant needs. It already contains the company API key, so treat it like a password &ndash; anyone with the URL reaches everything your company has
                            in Quaro.</template
                        >
                    </p>

                    <div v-if="!usePersonalKey && !isAdmin" class="not-admin-hint">
                        <v-icon small class="mr-1">mdi-lock-outline</v-icon>
                        Only admins can view or set up the MCP connection for this company right now.
                    </div>

                    <div v-else-if="loading && !apiKey" class="not-admin-hint">Loading your connection URL&hellip;</div>

                    <div v-else-if="apiKey">
                        <v-text-field :value="displayUrl" label="MCP server URL" readonly filled dense hide-details prepend-inner-icon="mdi-link-variant" aria-live="polite">
                            <template v-slot:append>
                                <v-btn
                                    icon
                                    x-small
                                    @click.stop.prevent="toggleUrl"
                                    :disabled="loading"
                                    :aria-label="showUrl ? 'Hide connection URL' : 'Show connection URL'"
                                    :title="showUrl ? 'Hide connection URL' : 'Show connection URL'">
                                    <v-icon small>{{ showUrl ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
                                </v-btn>

                                <v-btn icon x-small @click.stop.prevent="copyUrl" :disabled="loading" aria-label="Copy connection URL" title="Copy connection URL">
                                    <v-icon small>mdi-content-copy</v-icon>
                                </v-btn>
                            </template>
                        </v-text-field>

                        <div class="mt-4 d-flex align-center flex-wrap gap-2">
                            <v-btn color="primary" rounded @click="copyUrl" :disabled="loading">
                                <v-icon small class="mr-1">mdi-content-copy</v-icon>
                                Copy URL
                            </v-btn>
                            <span class="mcp-hint ml-3">
                                <v-icon x-small class="mr-1">mdi-alert-circle-outline</v-icon>
                                <template v-if="usePersonalKey">Regenerating your API key under <strong>Settings &rsaquo; API</strong> disconnects every assistant that uses this URL.</template>
                                <template v-else
                                    >Regenerating the company API key under <strong>Settings &rsaquo; API</strong> disconnects every assistant that uses this URL, for everyone in the
                                    company.</template
                                >
                            </span>
                        </div>
                    </div>

                    <div v-else-if="canCreateConnectionUrl">
                        <div class="d-flex align-center flex-wrap gap-2">
                            <v-btn color="primary" rounded @click="createApiKey" :loading="creating" :disabled="creating || loading">Create connection URL</v-btn>
                            <span class="mcp-hint ml-3">You can manage the API key later under <a href="#" class="docs-link" @click.prevent="$emit('navigate', 5)">Settings &rsaquo; API</a></span>
                        </div>
                    </div>

                    <div v-else-if="usePersonalKey && canCreatePersonalKey">
                        <p class="mb-3 not-admin-hint">
                            <v-icon small class="mr-1">mdi-alert-circle-outline</v-icon>
                            Could not load your connection URL. If you already have one, it still works.
                        </p>
                        <v-btn color="primary" rounded outlined @click="fetchApiKey">Try again</v-btn>
                    </div>

                    <div v-else-if="usePersonalKey" class="not-admin-hint">
                        <v-icon small class="mr-1">mdi-lock-outline</v-icon>
                        Your connection URL needs a personal API key, and your admin has not enabled those for users yet. Ask an admin to enable them under <strong>Settings &rsaquo; Account</strong>.
                    </div>

                    <div v-else class="not-admin-hint">
                        <v-icon small class="mr-1">mdi-alert-circle-outline</v-icon>
                        Could not load the company API key.
                        <a href="#" class="docs-link" @click.prevent="$emit('navigate', 5)">Check Settings &rsaquo; API</a>
                        or try reloading the page.
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <!-- setup guides -->
        <v-row class="mt-1">
            <v-col cols="12">
                <v-card outlined class="pa-4 mcp-card">
                    <div class="font-weight-bold text-h6 mb-1">Set up your assistant</div>
                    <p class="mb-4 mcp-help-text">
                        Pick your assistant and follow the steps. Setup takes about a minute. You never need OAuth or a separate login &ndash; whenever an assistant asks for authentication,
                        choose none. The key inside the URL does the job.
                    </p>
                    <p v-if="canCreateConnectionUrl" class="mb-4 mcp-hint">
                        <v-icon x-small class="mr-1">mdi-alert-circle-outline</v-icon>
                        Create your connection URL above first. The steps below use it.
                    </p>

                    <v-expansion-panels accordion flat class="mcp-panels">
                        <v-expansion-panel v-for="client in clients" :key="client.id">
                            <v-expansion-panel-header>
                                <div class="d-flex align-center">
                                    <v-icon color="primary" class="mr-3">{{ client.icon }}</v-icon>
                                    <div>
                                        <div class="font-weight-bold">{{ client.title }}</div>
                                        <div class="mcp-help-text mcp-small">{{ client.subtitle }}</div>
                                    </div>
                                </div>
                            </v-expansion-panel-header>

                            <v-expansion-panel-content>
                                <v-row>
                                    <v-col cols="12" md="7">
                                        <div class="requirement-line mb-3">
                                            <v-icon x-small class="mr-1">mdi-check-circle-outline</v-icon>
                                            {{ client.requirement }}
                                        </div>

                                        <ol class="mcp-steps">
                                            <li v-for="(step, index) in client.steps" :key="index">{{ step }}</li>
                                        </ol>

                                        <div v-if="client.snippet" class="mt-3">
                                            <div class="snippet-title">{{ client.snippetTitle }}</div>
                                            <pre class="snippet-box"><code>{{ client.snippet }}</code></pre>
                                            <v-btn small outlined color="primary" @click="copyText(client.snippet)">
                                                <v-icon x-small class="mr-1">mdi-content-copy</v-icon>
                                                Copy snippet
                                            </v-btn>
                                        </div>

                                        <div v-if="client.notes && client.notes.length" class="mt-3">
                                            <div v-for="(note, index) in client.notes" :key="index" class="client-note">
                                                <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                                                {{ note }}
                                            </div>
                                        </div>
                                    </v-col>

                                    <v-col cols="12" md="5">
                                        <img :src="client.image" :alt="client.imageAlt" class="mcp-illustration" />
                                        <p class="mcp-help-text mcp-small mt-2 mb-0">Schematic view &ndash; the exact wording may differ per assistant version.</p>
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>

                    <p class="mcp-help-text mcp-small mt-4 mb-0">
                        Any other assistant that supports remote MCP servers works the same way: add the connection URL as a remote server over HTTP, choose no authentication and you are done.
                    </p>
                </v-card>
            </v-col>
        </v-row>

        <!-- capabilities -->
        <v-row class="mt-1">
            <v-col cols="12">
                <v-card outlined class="pa-4 mcp-card">
                    <div class="font-weight-bold text-h6 mb-1">What your assistant can read</div>
                    <p class="mb-4 mcp-help-text">The connection covers every suite. Whatever you can open in Quaro, your assistant can read and work with.</p>

                    <v-row>
                        <v-col cols="12" md="6" v-for="capability in capabilities" :key="capability.suite">
                            <div class="capability-block">
                                <div class="font-weight-bold mb-1">
                                    <v-icon small color="primary" class="mr-1">{{ capability.icon }}</v-icon>
                                    {{ capability.suite }}
                                </div>
                                <ul class="capability-list">
                                    <li v-for="item in capability.items" :key="item">{{ item }}</li>
                                </ul>
                            </div>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>
        </v-row>

        <!-- example prompts -->
        <v-row class="mt-1">
            <v-col cols="12">
                <v-card outlined class="pa-4 mcp-card">
                    <div class="font-weight-bold text-h6 mb-1">What you can ask</div>
                    <p class="mb-4 mcp-help-text">Every example below is answered from your own Quaro data. Click a prompt to copy it.</p>

                    <div class="highlight-prompt mb-5" @click="copyText(crossSuitePrompt)" role="button" tabindex="0" @keyup.enter="copyText(crossSuitePrompt)">
                        <div class="highlight-label">
                            <v-icon x-small color="primary" class="mr-1">mdi-star-four-points</v-icon>
                            Across all suites &ndash; this is what a dashboard cannot do
                        </div>
                        <div class="highlight-text">{{ crossSuitePrompt }}</div>
                    </div>

                    <v-row>
                        <v-col cols="12" md="6" v-for="group in promptGroups" :key="group.suite">
                            <div class="font-weight-bold mb-2">
                                <v-icon small color="primary" class="mr-1">{{ group.icon }}</v-icon>
                                {{ group.suite }}
                            </div>
                            <div v-for="prompt in group.prompts" :key="prompt" class="prompt-chip" @click="copyText(prompt)" role="button" tabindex="0" @keyup.enter="copyText(prompt)">
                                {{ prompt }}
                                <v-icon x-small class="prompt-copy-icon">mdi-content-copy</v-icon>
                            </div>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>
        </v-row>

        <!-- security -->
        <v-row class="mt-1">
            <v-col cols="12">
                <v-card outlined class="pa-4 mcp-card">
                    <div class="font-weight-bold mb-2">Security and privacy</div>
                    <ul class="mcp-notes">
                        <li v-if="usePersonalKey"><strong>Your rights apply.</strong> Your assistant can only do what your role in Quaro allows. With a read-only role, every change is refused.</li>
                        <li><strong>Accounts stay separate.</strong> Every call runs under the API key in your URL, and every customer has a separate database.</li>
                        <li><strong>Logging.</strong> The MCP server stores no content from your calls. We log which tool ran, when, and whether it worked.</li>
                        <li>
                            <strong>Where data is processed.</strong> The MCP server and the Quaro API run in Germany. AI analyses and AI texts in Quaro are processed by OpenAI. OpenAI does not train
                            its models with this data.
                        </li>
                        <li>
                            <strong>Your own AI assistant.</strong> The answers you get from Quaro pass through the assistant you connect, so its provider handles them too, for example Anthropic if you
                            use Claude. Which assistant you use is your choice, and that provider's terms apply.
                        </li>
                        <li>
                            <strong>Confirming changes.</strong> In the Quaro AI Chat, a change only runs after you approve it with a click. In other assistants, their own settings decide, so check
                            what yours is about to do.
                        </li>
                    </ul>
                </v-card>
            </v-col>
        </v-row>

        <!-- faq -->
        <v-row class="mt-1">
            <v-col cols="12">
                <v-card outlined class="pa-4 mcp-card">
                    <div class="font-weight-bold mb-2">Good to know</div>
                    <ul class="mcp-notes">
                        <li>
                            The connection URL contains {{ usePersonalKey ? "your personal" : "the company" }} API key. Do not share it in tickets, screenshots or public repos.
                        </li>
                        <li>Your assistant sees the same data your Quaro plan includes &ndash; requests consume credits the same way the app does.</li>
                        <li>Need to cut off access? Regenerate your API key under <strong>Settings &rsaquo; API</strong>. All existing connections stop working immediately.</li>
                    </ul>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import claudeImage from "../assets/mcp/connect-claude.svg"
    import connectorDialogImage from "../assets/mcp/connect-chatgpt.svg"
    import ideImage from "../assets/mcp/connect-ide.svg"
    import { fetchPersonalApiKey, createOrRegeneratePersonalApiKey } from "./helper/personalApiKey"

    // VUE_APP_MCP_SERVER_URL lets dev.searchinsights.de point its connection URL at
    // the dev MCP instance (https://mcp.quaro.io/mcp2, see quaro-mcp-server's
    // Plan Teil 8) instead of prod's /mcp - without this override, a dev-key
    // pasted into a client would hit prod-Quaro and fail with 401.
    const MCP_BASE_URL = process.env.VUE_APP_MCP_SERVER_URL || "https://mcp.quaro.io/mcp"
    const KEY_PLACEHOLDER = "YOUR_CONNECTION_KEY"

    export default {
        data() {
            return {
                auth: "Basic " + window.btoa(process.env.VUE_APP_AUTH),
                showUrl: false,
                loading: false,
                // Nur fuer den "Create API key"-Button im Leer-Zustand (usePersonalKey), getrennt von
                // loading (das steckt schon im Fetch beim Mount).
                creating: false,
                benefits: [
                    {
                        icon: "mdi-message-text-outline",
                        title: "Just ask",
                        text: "No exports, no clicking through dashboards. Ask in plain language and get the answer from your live Quaro data."
                    },
                    {
                        icon: "mdi-vector-link",
                        title: "Combine your suites",
                        text: "Keyword research, rankings and AI visibility in one answer – the assistant pulls from all of them at once."
                    },
                    {
                        icon: "mdi-lightning-bolt-outline",
                        title: "Skip the busywork",
                        text: "Let the assistant do the comparing, filtering and summarising while you review the result."
                    }
                ],
                compatibility: [
                    {
                        title: "Ready to use",
                        icon: "mdi-check-circle",
                        iconColor: "primary",
                        cssClass: "tier-ready",
                        entries: [
                            { name: "Claude", note: "Web, desktop app and Claude Code – every plan" },
                            { name: "ChatGPT", note: "Paid plans, via developer mode" },
                            { name: "Perplexity", note: "Pro, Max and Enterprise" },
                            { name: "Mistral Le Chat", note: "Custom MCP connector" },
                            { name: "Cursor, Windsurf, Zed", note: "Config file in the editor" },
                            { name: "VS Code with GitHub Copilot", note: "mcp.json in the workspace" },
                            { name: "Gemini CLI, Codex CLI", note: "Command line tools" }
                        ]
                    },
                    {
                        title: "Works with limits",
                        icon: "mdi-alert-circle",
                        iconColor: "#ba7517",
                        cssClass: "tier-limited",
                        entries: [
                            {
                                name: "Gemini app (web and mobile)",
                                note: "Custom apps only with Gemini Spark: Google AI Pro or Ultra, personal Google account, US only. Not for Workspace accounts – use Gemini CLI instead"
                            },
                            { name: "Gemini Business and Enterprise", note: "An admin adds Quaro as a custom MCP connection in the admin console" },
                            { name: "Microsoft 365 Copilot", note: "Only through an agent built in Copilot Studio, with admin approval" }
                        ]
                    },
                    {
                        title: "Good to know",
                        icon: "mdi-information",
                        iconColor: "#5f5e5a",
                        cssClass: "tier-info",
                        entries: [
                            { name: "Free plans", note: "Claude allows one custom connector on the free plan. ChatGPT and Perplexity require a paid plan" },
                            { name: "Team plans", note: "In Claude Team and Enterprise an owner adds Quaro once for everyone – members never see the URL" },
                            { name: "Anything else", note: "If your assistant can add a remote MCP server by URL, it works. Choose no authentication" }
                        ]
                    }
                ],
                capabilities: [
                    {
                        suite: "Research Suite",
                        icon: "mdi-magnify",
                        items: [
                            "Keyword ideas and autocomplete suggestions for a topic, a domain or a single URL",
                            "Search volume, monthly history, trend, CPC, competition, difficulty and search intent",
                            "Your saved collections with all metrics, assigned categories and custom columns"
                        ]
                    },
                    {
                        suite: "Monitoring Suite",
                        icon: "mdi-chart-timeline-variant",
                        items: [
                            "The ranking table per crawl date with positions, search intent and SERP features",
                            "Visibility history, competitors and their top 10, 50 and 100 counts",
                            "Keyword and category trends, keyword clusters, AI Overviews and shopping sellers"
                        ]
                    },
                    {
                        suite: "AI Suite",
                        icon: "mdi-star-four-points",
                        items: [
                            "Brand visibility, relevance rank and sentiment across ChatGPT, Gemini, Perplexity, Copilot and Google AI",
                            "Prompts and their responses, plus the mentions you gained and lost between two dates",
                            "Cited source domains and URLs, page types, entities, fan-out queries and saved reports"
                        ]
                    },
                    {
                        suite: "Content Suite",
                        icon: "mdi-text-box-edit",
                        items: [
                            "Content projects with their settings, topics and competitor analysis",
                            "The stored WDF*IDF result per project: WDF, IDF, competitor coverage and term placement",
                            "Briefings and the current editor text"
                        ]
                    },
                    {
                        suite: "Account",
                        icon: "mdi-poll",
                        items: ["Your credit overview per suite, including what is left in the current billing cycle"]
                    }
                ],
                crossSuitePrompt:
                    "Take the keywords from my collection “Garden furniture”, check which of them rank outside the top 10 in my monitoring project, and suggest five content topics based on that.",
                promptGroups: [
                    {
                        suite: "Research Suite",
                        icon: "mdi-magnify",
                        prompts: [
                            "Find keyword ideas for “garden furniture” in Germany and show me everything above 1,000 monthly searches.",
                            "Get search volume, CPC and keyword difficulty for these 50 keywords and sort them by difficulty.",
                            "Which keywords in my collection “Summer 2026” have the highest search volume, and which categories do they fall into?"
                        ]
                    },
                    {
                        suite: "Monitoring Suite",
                        icon: "mdi-chart-timeline-variant",
                        prompts: [
                            "Compare the rankings in my project “Onlineshop DE” between the last two crawl dates and show me the biggest losses.",
                            "Which competitors have the most top 10 rankings in my monitoring project, and how did their visibility develop?",
                            "Show all keywords in my project where I rank between position 4 and 10 and the search intent is transactional."
                        ]
                    },
                    {
                        suite: "AI Suite",
                        icon: "mdi-star-four-points",
                        prompts: [
                            "How visible is my brand in ChatGPT, Gemini and Perplexity, and how did that develop over the last weeks?",
                            "Which brand mentions did I gain and which did I lose between the last two dates?",
                            "Which source domains get cited most often in the AI answers for my project?"
                        ]
                    },
                    {
                        suite: "Content Suite",
                        icon: "mdi-text-box-edit",
                        prompts: [
                            "Show me the briefing and the selected terms of my content project “heat pump costs”.",
                            "Which WDF*IDF terms in that project have the weakest coverage compared to the competitors?",
                            "How do the competitors in my content project compare in word count and page speed?"
                        ]
                    },
                    {
                        suite: "Account",
                        icon: "mdi-poll",
                        prompts: ["How many credits do I have left in each suite this billing cycle?"]
                    }
                ],
                faq: [
                    {
                        question: "What is MCP and do I need to understand it?",
                        answer:
                            "MCP (Model Context Protocol) is the open standard AI assistants use to talk to external tools – think of it as the plug that fits every assistant. You do not need to understand it: add the connection URL once in your assistant and then ask questions as you normally would."
                    },
                    {
                        question: "Can the assistant change something in Quaro?",
                        answer:
                            "Yes. With your user rights your assistant can create, edit and delete collections, projects, prompts and briefings. Deleting cannot be undone, so check what your assistant is about to delete. Changes show up in the app like changes you make yourself, and actions that use credits count against your plan."
                    },
                    {
                        question: "Quaro does not show up in my chat",
                        answer:
                            "Most assistants switch connectors on per conversation. In Claude, click the + button at the lower left of the chat, open Connectors and enable Quaro. In ChatGPT and Perplexity, pick Quaro from the tools menu next to the message box. In Claude Code or Gemini CLI, run /mcp to see whether Quaro is listed and connected."
                    },
                    {
                        question: "The connection fails or the assistant says it cannot reach the server",
                        answer:
                            "Check that you copied the complete URL including the ?key= part – it is easy to lose the end of it. If your assistant asks for authentication, choose none. If an admin regenerated the API key under Settings › API, the old URL stops working and you need to add the new one."
                    },
                    {
                        question: "Does the assistant use my Quaro credits?",
                        answer:
                            "Reading data you already have in Quaro costs nothing: collections, rankings, AI visibility and content projects are free. Fresh lookups behave exactly as in the app – keyword ideas, suggests and search volume draw from your Research Suite credits. Just ask your assistant how many credits are left whenever you want to check."
                    },
                    {
                        question: "Which data does the AI provider see?",
                        answer:
                            "Everything Quaro returns to your assistant travels through the provider of that assistant – Anthropic for Claude, OpenAI for ChatGPT and so on – just like anything you paste into a chat. If your company policy does not allow sharing SEO data with these providers, do not connect Quaro."
                    },
                    {
                        question: "Does my assistant see everything my company has in Quaro?",
                        answer:
                            "Yes. The connection uses the company API key, so it reaches the same data and suites your company has booked – it does not narrow the view down to a single user. Only share the URL with people who are allowed to see that data."
                    },
                    {
                        question: "Can I use it in my team without sharing the key with everyone?",
                        answer:
                            "On Claude Team and Enterprise plans an owner adds the connector once under Organization settings › Connectors, and members enable it from Customize › Connectors without ever seeing the URL. Gemini Business and Enterprise and Microsoft 365 Copilot work the same way through their admin consoles. On all other plans every user needs the URL."
                    },
                    {
                        question: "How do I revoke access?",
                        answer:
                            "Regenerate the API key under Settings › API. Every assistant that uses the old URL loses access immediately. Removing the connector in one assistant only disconnects that one client."
                    }
                ]
            }
        },
        computed: {
            isAdmin() {
                return !!(this.$store && this.$store.state && this.$store.state.admin)
            },
            // Personal-Keys aktiv (user_api_key_feature_enabled im Store) -> MCP nutzt wie im urspruenglichen
            // Teil-3-Design den persoenlichen Key jedes Users (kein Admin-Gate). Deaktiviert -> Fallback auf
            // den Company-Key, admin-only, wie im API-Tab. Beide Zweige bleiben erhalten, damit ein spaeteres
            // erneutes Deaktivieren wieder ein reiner Store-Flag-Flip ist, kein Code-Umbau.
            usePersonalKey() {
                return !!(this.$store && this.$store.state && this.$store.state.user_api_key_feature_enabled)
            },
            // Ob dieser User im Leer-Zustand ueberhaupt "Create API key" sehen darf - Admins immer, sonst
            // nur wenn der Admin die Key-Verwaltung fuer User freigegeben hat (Settings > Account). Ohne
            // diese Pruefung wuerde der Backend-403 erst nach dem Klick kommen.
            canCreatePersonalKey() {
                return !!(this.$store && this.$store.state && (this.$store.state.admin || this.$store.state.allow_user_api_key))
            },
            // Leer-Zustand: sicher noch keine URL ("" im Store), und der User kann sie selbst anlegen. Nicht
            // waehrend des Ladens und nicht nach einem Ladefehler (null) - das Anlegen ersetzt einen vorhandenen
            // Key, alle verbundenen Assistenten wuerden den Zugang verlieren.
            canCreateConnectionUrl() {
                return this.usePersonalKey && this.canCreatePersonalKey && this.$store.state.personal_api_key === "" && !this.loading
            },
            // Aus dem Store statt lokalem data() - Vuetify haelt diesen Tab nach dem ersten Oeffnen im
            // Speicher (kein Re-Mount), ein Regenerate auf der API-Seite muss hier deshalb ohne Reload
            // ankommen. Siehe Kommentar an personal_api_key/company_api_key im Store.
            apiKey() {
                if (this.usePersonalKey) return this.$store.state.personal_api_key || ""
                return this.$store.state.company_api_key || ""
            },
            connectionUrl() {
                return MCP_BASE_URL + "?key=" + (this.apiKey || KEY_PLACEHOLDER)
            },
            displayUrl() {
                if (!this.apiKey) return MCP_BASE_URL + "?key=" + KEY_PLACEHOLDER
                if (this.showUrl) return this.connectionUrl
                return MCP_BASE_URL + "?key=" + "*".repeat(this.apiKey.length)
            },
            snippetUrl() {
                return this.apiKey ? this.connectionUrl : MCP_BASE_URL + "?key=" + KEY_PLACEHOLDER
            },
            cursorJsonSnippet() {
                return ["{", '  "mcpServers": {', '    "quaro": {', '      "url": "' + this.snippetUrl + '"', "    }", "  }", "}"].join("\n")
            },
            geminiJsonSnippet() {
                return ["{", '  "mcpServers": {', '    "quaro": {', '      "httpUrl": "' + this.snippetUrl + '"', "    }", "  }", "}"].join("\n")
            },
            claudeCodeCliSnippet() {
                return 'claude mcp add --transport http --scope user quaro "' + this.snippetUrl + '"'
            },
            geminiCliSnippet() {
                return 'gemini mcp add --transport http quaro "' + this.snippetUrl + '"'
            },
            clients() {
                return [
                    {
                        id: "claude",
                        icon: "mdi-message-star-outline",
                        title: "Claude",
                        subtitle: "claude.ai, desktop app and Claude Code",
                        image: claudeImage,
                        imageAlt: "Schematic view of the Claude settings with the connectors section and a field for the server URL",
                        requirement: "Works on every Claude plan. The free plan allows one custom connector.",
                        steps: [
                            "In Claude, open Customize › Connectors (also reachable at claude.ai/customize/connectors).",
                            "Click the + button and choose Add custom connector.",
                            "Name it Quaro and paste your connection URL as the remote MCP server URL. Leave Advanced settings empty – no OAuth is needed.",
                            "Click Add. In a chat, click the + button at the lower left, open Connectors and switch Quaro on."
                        ],
                        snippetTitle: "Claude Code without a claude.ai login",
                        snippet: this.claudeCodeCliSnippet,
                        notes: [
                            "Claude Code picks up the connector automatically once it is added in claude.ai – no separate setup. Run /mcp in Claude Code to check. The command above is only needed if Claude Code is not logged in with your claude.ai account.",
                            "On Team and Enterprise plans an owner adds the connector under Organization settings › Connectors › Add › Custom › Web. Members then find it under Customize › Connectors and click Connect."
                        ]
                    },
                    {
                        id: "chatgpt",
                        icon: "mdi-robot-outline",
                        title: "ChatGPT",
                        subtitle: "Custom connector via developer mode",
                        image: connectorDialogImage,
                        imageAlt: "Schematic view of a dialog for adding a custom connector with a name field, a URL field and a confirmation toggle",
                        requirement: "Requires a paid plan (Plus, Pro, Business, Enterprise or Edu). In a workspace an admin has to allow custom MCP connectors first.",
                        steps: [
                            "Enable developer mode: Settings › Apps › Advanced settings › Developer mode. OpenAI moves this toggle now and then – if it is not there, look under Connectors › Advanced or Security and login.",
                            "Open Settings › Connectors and click Create.",
                            "Name it Quaro, add a short description such as “SEO and AI visibility data from Quaro”, paste your connection URL as the MCP server URL and set Authentication to No authentication.",
                            "Accept the notice about third-party connectors. In a chat, open the tools menu next to the message box and select Quaro."
                        ],
                        notes: ["If Quaro's tools change later, open the connector under Settings › Connectors and click Refresh."]
                    },
                    {
                        id: "perplexity",
                        icon: "mdi-compass-outline",
                        title: "Perplexity",
                        subtitle: "Custom remote connector",
                        image: connectorDialogImage,
                        imageAlt: "Schematic view of a dialog for adding a custom connector with a name field, a URL field and a confirmation toggle",
                        requirement: "Requires Perplexity Pro, Max or Enterprise.",
                        steps: [
                            "Open your account settings, choose All settings and go to Connectors.",
                            "Click + Custom connector at the top right and select Remote.",
                            "Name it Quaro, paste your connection URL as the MCP server URL, set Authentication to None and Transport to Streamable HTTP.",
                            "Confirm the acknowledgement and click Add. In a chat, select Quaro from the sources or tools menu."
                        ],
                        notes: []
                    },
                    {
                        id: "lechat",
                        icon: "mdi-weather-windy",
                        title: "Mistral Le Chat",
                        subtitle: "Custom MCP connector",
                        image: connectorDialogImage,
                        imageAlt: "Schematic view of a dialog for adding a custom connector with a name field, a URL field and a confirmation toggle",
                        requirement: "Available in Le Chat's Connectors settings.",
                        steps: [
                            "Open the Connectors page in Le Chat and click + Add Connector.",
                            "Switch to the Custom MCP Connector tab.",
                            "Use quaro as the connector name (no spaces), paste your connection URL as the server URL and add a short description.",
                            "Click Connect. Le Chat detects that no authentication is needed. In a chat, enable Quaro in the tools menu."
                        ],
                        notes: []
                    },
                    {
                        id: "gemini",
                        icon: "mdi-google",
                        title: "Gemini",
                        subtitle: "Gemini CLI – the web app only with limits",
                        image: ideImage,
                        imageAlt: "Schematic view of a code editor showing a JSON configuration file for the MCP server",
                        requirement: "Gemini CLI works on every plan. The Gemini web and mobile app only accepts custom apps through Gemini Spark (Google AI Pro or Ultra, personal Google account, US only).",
                        steps: [
                            "Gemini CLI: run the command below, or add the JSON entry to ~/.gemini/settings.json.",
                            "Run /mcp inside Gemini CLI to confirm that Quaro is connected.",
                            "Gemini app with Spark access: open Settings & help › Connected Apps › Custom apps for Spark › Add a custom app and paste your connection URL.",
                            "Gemini Business or Enterprise: ask your admin to add Quaro as a custom MCP connection in the admin console."
                        ],
                        snippetTitle: "Gemini CLI",
                        snippet: this.geminiCliSnippet + "\n\n# or in ~/.gemini/settings.json\n" + this.geminiJsonSnippet,
                        notes: ["Google Workspace accounts cannot add custom apps in the Gemini app at the moment – Gemini CLI is the way to go there."]
                    },
                    {
                        id: "ide",
                        icon: "mdi-code-braces",
                        title: "Cursor, VS Code, Windsurf, Zed and other editors",
                        subtitle: "Configure via a JSON file",
                        image: ideImage,
                        imageAlt: "Schematic view of a code editor showing a JSON configuration file for the MCP server",
                        requirement: "Works with every editor or agent that supports remote MCP servers over HTTP.",
                        steps: [
                            "Open the MCP configuration of your editor – in Cursor that is .cursor/mcp.json inside a project or ~/.cursor/mcp.json for all projects.",
                            "Add the entry below and save the file.",
                            "Restart the editor or reload its MCP servers so it picks up Quaro.",
                            "VS Code with GitHub Copilot uses .vscode/mcp.json with a servers key instead of mcpServers and needs \"type\": \"http\" in the entry."
                        ],
                        snippetTitle: "mcp.json",
                        snippet: this.cursorJsonSnippet,
                        notes: ["If your editor asks for a transport type, choose http or streamable-http. No headers or tokens are needed."]
                    }
                ]
            }
        },
        methods: {
            createInfo(message, color, icon) {
                this.$emit("info", message, color, icon)
            },
            toggleUrl() {
                this.showUrl = !this.showUrl
            },
            async copyText(value) {
                try {
                    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                        await navigator.clipboard.writeText(value)
                    } else {
                        const helperInput = document.createElement("input")
                        helperInput.value = value
                        document.body.appendChild(helperInput)
                        helperInput.select()
                        document.execCommand("copy")
                        document.body.removeChild(helperInput)
                    }
                    this.createInfo("Copied to clipboard.", "green", "mdi-check-circle-outline")
                } catch (error) {
                    this.createInfo("Failed to copy.", "#fcba03", "mdi-alert-circle-outline")
                }
            },
            async copyUrl() {
                if (!this.apiKey) {
                    this.createInfo("No API key available.", "#fcba03", "mdi-alert-circle-outline")
                    return
                }
                await this.copyText(this.connectionUrl)
            },
            // Personal- oder Company-Key laden, je nach usePersonalKey - schreibt beide Male in den Store
            // statt in lokales data() (siehe computed apiKey), damit ein bereits offener API-Tab das
            // Ergebnis auch sieht.
            fetchApiKey() {
                if (this.usePersonalKey) {
                    this.loading = true
                    fetchPersonalApiKey(this.$http, this.auth)
                        .then(() => {
                            this.showUrl = false
                        })
                        .catch((error) => {
                            this.createInfo(error.message, "#fcba03", "mdi-alert-circle-outline")
                        })
                        .finally(() => {
                            this.loading = false
                        })
                    return
                }
                // Company-Key: gleiches Muster wie fetchCompanyApiKey in ApiKeySettings.vue.
                if (!this.isAdmin) return
                this.loading = true
                this.$http
                    .post(process.env.VUE_APP_APIURL + "/company/apikey", { company_id: localStorage.company_id }, { headers: { Authorization: this.auth } })
                    .then((response) => {
                        this.$store.commit("SET_COMPANY_API_KEY", (response.body && response.body.api_key) || "")
                        this.showUrl = false
                    })
                    .catch((error) => {
                        console.log(error)
                        this.createInfo("Failed to load the company API key.", "#fcba03", "mdi-alert-circle-outline")
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },
            // Erstanlage direkt aus dem MCP-Tab (Leer-Zustand, siehe canCreatePersonalKey) - kein Dialog,
            // es gibt beim allerersten Key nichts zu verlieren. Gleicher Endpunkt/Upsert wie ein Regenerate
            // im API-Tab, deshalb derselbe Helfer.
            createApiKey() {
                this.creating = true
                createOrRegeneratePersonalApiKey(this.$http, this.auth)
                    .then(() => {
                        this.showUrl = false
                        this.createInfo("Your connection URL is ready.", "green", "mdi-check-circle-outline")
                    })
                    .catch((error) => {
                        this.createInfo(error.message, "#fcba03", "mdi-alert-circle-outline")
                    })
                    .finally(() => {
                        this.creating = false
                    })
            }
        },
        mounted() {
            // Personal-Key: nur laden, wenn noch nie geholt (null) - der Store ist danach die Wahrheit, ein
            // Regenerate/Create im API-Tab schreibt ohnehin hinein. Company-Key: Store hat ihn evtl. schon
            // (API-Tab zuerst besucht, oder Regenerate waehrend dieser Tab schon offen war) -> kein
            // unnoetiger Request.
            if (this.usePersonalKey && this.$store.state.personal_api_key !== null) return
            if (!this.usePersonalKey && this.$store.state.company_api_key) return
            this.fetchApiKey()
        }
    }
</script>

<style scoped>
    .mcp-page {
        max-width: 1360px;
    }

    .mcp-help-text {
        color: #4c4c4c;
    }

    .mcp-small {
        font-size: 13px;
    }

    .home-like-header {
        background: linear-gradient(90deg, rgb(245 254 251 / 90%) 0%, rgb(249 245 247 / 90%) 100%);
        border: 1px solid #e9efed;
        border-radius: 12px;
        padding: 14px 16px;
    }

    .header-eyebrow {
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        color: #0f6e56;
    }

    .mcp-card {
        border-radius: 12px;
    }

    .read-only-card {
        border-color: #b7ded3;
        background: rgb(225 245 238 / 35%);
    }

    .mcp-hint {
        color: #4c4c4c;
        font-size: 13px;
    }

    .not-admin-hint {
        color: #4c4c4c;
        font-size: 14px;
    }

    .tier-block {
        border: 1px solid #e4e8ef;
        border-radius: 10px;
        padding: 12px 14px;
        height: 100%;
    }

    .tier-ready {
        background: rgb(225 245 238 / 35%);
        border-color: #b7ded3;
    }

    .tier-limited {
        background: rgb(250 238 218 / 45%);
        border-color: #f0d9a8;
    }

    .tier-title {
        font-size: 14px;
        font-weight: 700;
        color: #333333;
    }

    .tier-entry {
        display: flex;
        flex-direction: column;
        margin-bottom: 8px;
    }

    .tier-entry-name {
        font-size: 13px;
        font-weight: 600;
        color: #333333;
    }

    .tier-entry-note {
        font-size: 12px;
        color: #4c4c4c;
        line-height: 1.45;
    }

    .mcp-panels {
        border: 1px solid #e4e8ef;
        border-radius: 10px;
        overflow: hidden;
    }

    .mcp-panels .v-expansion-panel + .v-expansion-panel {
        border-top: 1px solid #e4e8ef;
    }

    .faq-header {
        font-size: 14px;
        font-weight: 600;
        color: #333333;
    }

    .faq-answer {
        font-size: 14px;
        line-height: 1.7;
    }

    .requirement-line {
        font-size: 13px;
        color: #0f6e56;
        background: rgb(225 245 238 / 45%);
        border-radius: 8px;
        padding: 6px 10px;
    }

    .client-note {
        font-size: 13px;
        color: #4c4c4c;
        margin-bottom: 6px;
        line-height: 1.5;
    }

    .mcp-steps {
        padding-left: 18px;
        margin: 0;
        color: #4c4c4c;
        font-size: 14px;
        line-height: 1.7;
    }

    .mcp-steps li + li {
        margin-top: 4px;
    }

    .capability-block {
        border: 1px solid #e4e8ef;
        border-radius: 10px;
        padding: 12px 14px;
        height: 100%;
        margin-bottom: 12px;
    }

    .capability-list {
        padding-left: 18px;
        margin: 0;
        color: #4c4c4c;
        font-size: 13px;
        line-height: 1.6;
    }

    .mcp-illustration {
        width: 100%;
        max-width: 320px;
        height: auto;
        border-radius: 8px;
    }

    .snippet-title {
        font-weight: 700;
        margin-bottom: 8px;
        font-size: 13px;
    }

    .snippet-box {
        background: #f6f8fb;
        border: 1px solid #e4e8ef;
        border-radius: 10px;
        padding: 12px;
        font-size: 12px;
        line-height: 1.45;
        white-space: pre-wrap;
        word-break: break-word;
        margin-bottom: 10px;
    }

    .highlight-prompt {
        border: 1px solid #b7ded3;
        background: rgb(225 245 238 / 45%);
        border-radius: 10px;
        padding: 12px 14px;
        cursor: pointer;
    }

    .highlight-prompt:hover,
    .highlight-prompt:focus {
        background: rgb(225 245 238 / 80%);
    }

    .highlight-label {
        font-size: 12px;
        font-weight: 700;
        color: #0f6e56;
        margin-bottom: 4px;
    }

    .highlight-text {
        font-size: 14px;
        color: #333333;
    }

    .prompt-chip {
        position: relative;
        border: 1px solid #e4e8ef;
        border-radius: 10px;
        padding: 9px 34px 9px 12px;
        margin-bottom: 8px;
        font-size: 13px;
        color: #4c4c4c;
        cursor: pointer;
        transition: border-color 0.15s ease;
    }

    .prompt-chip:hover,
    .prompt-chip:focus {
        border-color: #008470;
    }

    .prompt-copy-icon {
        position: absolute;
        right: 10px;
        top: 11px;
        opacity: 0;
        transition: opacity 0.15s ease;
    }

    .prompt-chip:hover .prompt-copy-icon,
    .prompt-chip:focus .prompt-copy-icon {
        opacity: 0.6;
    }

    .gap-2 {
        gap: 8px;
    }
</style>
